import { EtcOutboundEditableRow } from '@/services/outbound/outboundEtc/types'
import { TRANSPORT_TYPE_OPTIONS } from '@/services/outbound/outboundEtc/consts'
import { REQUIRED_COLUMNS } from '@/pages/outbound/outboundEtc/components/columns'

/**
 * 전화·핸드폰 형식 — 1.0 `ValidationUtil.getPhoneValidate`. 숫자와 공백·`-`·`+`·`*` 만 허용한다.
 * `*` 를 허용하는 건 개인정보 마스킹(`010-****-1234`)된 조회 결과를 그대로 다시 저장할 수 있어서다.
 */
const PHONE_PATTERN = /^[0-9\s\-+*]*$/

/** 우편번호 형식 — 1.0 `ValidationUtil.getZipCodeValidate`. 숫자와 `-`·`*` 만 허용한다. */
const ZIP_CODE_PATTERN = /^[0-9\-*]*$/

const isBlank = (value: unknown) => value == null || String(value).trim() === ''

export const isValidPhone = (value: unknown) => isBlank(value) || PHONE_PATTERN.test(String(value).trim())

export const isValidZipCode = (value: unknown) => isBlank(value) || ZIP_CODE_PATTERN.test(String(value).trim())

/** 저장 직전 검증 — 1.0 `GI35.onHandleSave` 는 우편번호만 본다. */
export const findInvalidZipCodeRow = (rows: EtcOutboundEditableRow[]) => rows.find((row) => !isValidZipCode(row.TRS_ZIP))

/** 확정 직전 검증 — 1.0 `GI35.onHandleSend` 는 전화·핸드폰을 먼저 보고 그다음 우편번호를 본다. */
export const findInvalidPhoneRow = (rows: EtcOutboundEditableRow[]) =>
  rows.find((row) => !isValidPhone(row.TRS_MOBILE) || !isValidPhone(row.TRS_PHONE))

/**
 * 확정 가능한 배송유형 — BE `TransportType` enum 실측과 같은 목록(`TRANSPORT_TYPE_OPTIONS`)을 그대로 쓴다.
 *
 * GI31 `CONFIRMABLE_TRANSPORT_TYPES`(택배·택배-단포·용차 3종, `generalOutbound/components/consts.ts:46`)와는
 * 목적이 다르다 — 그건 "일반출고 확정을 허용하는 배송유형"이라는 업무 규칙이고, 여기는 "BE 가 실제로
 * 아는 배송유형 코드인가"라는 데이터 계약이다. 여기서 GI31 목록을 재사용하면 새벽배송·특송처럼
 * 정상적으로 확정돼야 할 값까지 막혀, 이 티켓이 막으려는 사고(빈 값이 택배로 잘못 채번)보다 더 큰
 * 회귀가 된다. 그래서 공용화하지 않는다.
 */
const VALID_TRANSPORT_TYPE_CODES = new Set(TRANSPORT_TYPE_OPTIONS.map(({ value }) => value))

export interface InvalidTransportTypeRow {
  key: string
  orderNumber: string
  /** 빈 값이면 빈 문자열. */
  value: string
}

/**
 * 확정 직전 배송유형 검사 — 빈 값과 허용 목록 밖의 값을 둘 다 막는다.
 *
 * BE `TransportType.fromCode(value)` 는 목록에 없는 값(빈 값 포함)을 조용히 '택배'로 떨어뜨린다.
 * 그 값으로 컷오프가 택배사를 고르므로, 새벽배송·용차·특송 주문이 잘못된 택배사로 운송장 채번되고
 * 되돌릴 수 없다(LOGISPRD-11888). 원래 있던 화면 검증이 `51779baf` 에서 빠지며 드러났다.
 *
 * 1.0/구 화면 검증은 세션 화주가 4곳에 들 때만, 그것도 조회 조건 스냅샷(`searchParams.TRS_TYPE`)으로
 * 봤다. 이번엔 화주와 무관하게 확정 대상 행 값을 직접 본다 — 엑셀 업로드·셀 편집으로 들어온 값도
 * 걸러야 해서다. 필터값만 보면 그 값들을 놓친다.
 */
export const findInvalidTransportTypeRow = (rows: EtcOutboundEditableRow[]): InvalidTransportTypeRow | undefined => {
  const row = rows.find((r) => !VALID_TRANSPORT_TYPE_CODES.has(String(r.TRS_TYPE ?? '').trim()))
  if (!row) return undefined
  return { key: row.key, orderNumber: row.ORD_NO, value: String(row.TRS_TYPE ?? '').trim() }
}

/** 주문수량은 빈 값이 `Number('') === 0` 으로 새어 0 건 저장이 시도된다. 0 이하는 미입력으로 본다. */
const QUANTITY_KEY = 'ORD_QTY'

const isMissing = (key: string, value: unknown) => {
  if (key === QUANTITY_KEY) return !(Number(value) > 0)
  return value == null || String(value).trim() === ''
}

export interface MissingRequiredCell {
  /**
   * 비어 있는 칸이 있는 행의 키.
   *
   * 행 번호를 여기서 매기면 안 된다 — 호출부가 편집한 행만 걸러 넘기므로(`rows.filter(row => row.EDITTED)`)
   * 그 안의 순번은 화면에 보이는 행 번호와 다르다. 200행 중 12행과 87행만 고치면 87행이 2번째가 된다.
   * 번호는 전체 행을 아는 호출부가 매긴다.
   */
  key: string
  /** 표 헤더 제목. */
  title: string
}

/**
 * 저장 전 필수값 검사. 첫 번째로 비어 있는 칸을 돌려준다.
 *
 * 표 헤더의 별표와 같은 출처(`REQUIRED_COLUMNS`)를 쓴다 — 별표는 붙는데 검사가 없으면
 * 사용자는 필수로 아는 칸을 비운 채 저장을 시도하고 BE 400 으로 튕긴다.
 * 1.0 `CommonUtil.onSaveData` 도 행 번호와 컬럼명을 함께 알리고 저장을 중단했다.
 */
export const findMissingRequired = (rows: EtcOutboundEditableRow[]): MissingRequiredCell | undefined =>
  rows
    .map((row) => {
      const column = REQUIRED_COLUMNS.find(({ key }) => isMissing(key, row[key as keyof EtcOutboundEditableRow]))
      return column ? { key: row.key, title: column.title } : undefined
    })
    .find(Boolean)

/** 한글 받침 유무로 주격조사를 고른다 — "상품명가" 같은 문구를 막는다. */
export const subjectParticle = (word: string): string => {
  const last = word.charCodeAt(word.length - 1)
  const isHangulSyllable = last >= 0xac00 && last <= 0xd7a3
  if (!isHangulSyllable) return '가'
  return (last - 0xac00) % 28 === 0 ? '가' : '이'
}
